import React from 'react';
import { clsx } from 'clsx';
import { Eye, EyeOff } from 'lucide-react';

interface InputProps extends React.InputHTMLAttributes<HTMLInputElement> {
    label?: string;
    error?: string;
    helperText?: string;
    leftIcon?: React.ReactNode;
    rightIcon?: React.ReactNode;
    fullWidth?: boolean;
    variant?: 'default' | 'filled' | 'underlined';
    inputSize?: 'sm' | 'md' | 'lg';
}

const Input = React.forwardRef<HTMLInputElement, InputProps>(
    ({
        label,
        error,
        helperText,
        leftIcon,
        rightIcon,
        fullWidth = false,
        variant = 'default',
        inputSize = 'md',
        className,
        type,
        id,
        ...props
    }, ref) => {
        const [showPassword, setShowPassword] = React.useState(false);
        const [inputId] = React.useState(id || `input-${Math.random().toString(36).substr(2, 9)}`);

        const isPassword = type === 'password';
        const actualType = isPassword && showPassword ? 'text' : type;

        const baseClasses = [
            'block w-full transition-all duration-200',
            'focus:outline-none focus:ring-2 focus:ring-offset-0',
            'disabled:opacity-50 disabled:cursor-not-allowed'
        ];

        const variantClasses = {
            default: [
                'border border-gray-300 rounded-lg bg-white',
                'focus:ring-primary-500 focus:border-primary-500',
                error ? 'border-red-500 focus:ring-red-500 focus:border-red-500' : ''
            ],
            filled: [
                'border-0 rounded-lg bg-gray-100',
                'focus:ring-primary-500 focus:bg-white',
                error ? 'bg-red-50 focus:ring-red-500' : ''
            ],
            underlined: [
                'border-0 border-b-2 border-gray-300 rounded-none bg-transparent',
                'focus:ring-0 focus:border-primary-500',
                error ? 'border-red-500 focus:border-red-500' : ''
            ]
        };

        const sizeClasses = {
            sm: 'px-3 py-2 text-sm',
            md: 'px-4 py-3 text-base',
            lg: 'px-5 py-4 text-lg'
        };

        const iconPaddingClasses = {
            sm: leftIcon ? 'pl-10' : rightIcon || isPassword ? 'pr-10' : '',
            md: leftIcon ? 'pl-12' : rightIcon || isPassword ? 'pr-12' : '',
            lg: leftIcon ? 'pl-14' : rightIcon || isPassword ? 'pr-14' : ''
        };

        const iconSizeClasses = {
            sm: 'w-4 h-4',
            md: 'w-5 h-5',
            lg: 'w-6 h-6'
        };

        const iconPositionClasses = {
            sm: 'top-2 left-3',
            md: 'top-3 left-4',
            lg: 'top-4 left-5'
        };

        const rightIconPositionClasses = {
            sm: 'top-2 right-3',
            md: 'top-3 right-4',
            lg: 'top-4 right-5'
        };

        const inputClasses = clsx(
            baseClasses,
            variantClasses[variant],
            sizeClasses[inputSize],
            iconPaddingClasses[inputSize],
            fullWidth ? 'w-full' : '',
            className
        );

        const iconClasses = clsx(
            'absolute pointer-events-none text-gray-400',
            iconSizeClasses[inputSize]
        );

        const rightIconClasses = clsx(
            'absolute text-gray-400',
            iconSizeClasses[inputSize],
            rightIconPositionClasses[inputSize],
            isPassword || rightIcon ? 'cursor-pointer pointer-events-auto' : 'pointer-events-none'
        );

        return (
            <div className={fullWidth ? 'w-full' : ''}>
                {label && (
                    <label
                        htmlFor={inputId}
                        className="block text-sm font-medium text-gray-700 mb-2"
                    >
                        {label}
                    </label>
                )}

                <div className="relative">
                    {leftIcon && (
                        <div className={clsx(iconClasses, iconPositionClasses[inputSize])}>
                            {leftIcon}
                        </div>
                    )}

                    <input
                        ref={ref}
                        id={inputId}
                        type={actualType}
                        className={inputClasses}
                        {...props}
                    />

                    {(rightIcon || isPassword) && (
                        <div
                            className={rightIconClasses}
                            onClick={isPassword ? () => setShowPassword(!showPassword) : undefined}
                        >
                            {isPassword ? (
                                showPassword ? <EyeOff className={iconSizeClasses[inputSize]} /> : <Eye className={iconSizeClasses[inputSize]} />
                            ) : (
                                rightIcon
                            )}
                        </div>
                    )}
                </div>

                {(error || helperText) && (
                    <p className={clsx(
                        'mt-2 text-sm',
                        error ? 'text-red-600' : 'text-gray-500'
                    )}>
                        {error || helperText}
                    </p>
                )}
            </div>
        );
    }
);

Input.displayName = 'Input';

export default Input;